import React from "react";
import { Volume2, VolumeX } from "lucide-react";
import demoVideo from "../../assets/Podcast_Intro.mp4";
import handCursor from "../../assets/hand-cursor 1.png";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import useVideoMute from "../../hooks/useVideoMute";

const DemoNow = () => {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.3 });
  const { ref: demoRef, isVisible: demoVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.5 });
  
  // Video mute hook for demo video
  const demoVideoMute = useVideoMute('demo-video');

  return (
    <div className="bg-black flex py-8 sm:py-12 md:py-16 lg:py-20 xl:py-22 2xl:py-24 flex-col items-center justify-center px-4 sm:px-6">
      {/* Title with fade-in animation */}
      <p 
        ref={titleRef}
        className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-ag-black-300 font-sourceSans font-medium max-w-xs sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto mb-6 sm:mb-8 md:mb-10 lg:mb-12 text-center transition-all duration-700 ${
          titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        This is what you can make in minutes
      </p>
      
      {/* Demo card with scale animation and hover effect */}
      <div
        ref={demoRef}
        className={`w-[95%] sm:w-[85%] md:w-[75%] lg:w-[70%] xl:w-[68%] 2xl:w-[65%] px-2 sm:px-4 lg:px-6 xl:px-8 relative transition-all duration-1000 ${
          demoVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        <div className="relative hover:scale-[1.02] transition-transform duration-500 hover:shadow-2xl hover:shadow-ag-green/10 rounded-2xl overflow-hidden">
          <div className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden">
            <video
              ref={demoVideoMute.videoRef}
              src={demoVideo}
              autoPlay
              loop
              muted={demoVideoMute.isMuted}
              playsInline
              className="w-full h-full object-cover"
            />
            {/* Unmute button - top right corner */}
            <button
              onClick={demoVideoMute.toggleMute}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-5 md:right-5 bg-black/70 hover:bg-black/90 text-white rounded-full p-2.5 sm:p-3 md:p-3.5 transition-all duration-300 hover:scale-110 z-10 backdrop-blur-sm"
              aria-label={demoVideoMute.isMuted ? "Unmute video" : "Mute video"}
            >
              {demoVideoMute.isMuted ? (
                <VolumeX className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7" />
              ) : (
                <Volume2 className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7" />
              )}
            </button>
          </div>
        </div>
        {/* Animated hand cursor */}
        <img
          src={handCursor}
          alt="Hand cursor"
          className={`absolute right-0 bottom-0 w-10 sm:w-12 md:w-14 lg:w-16 xl:w-18 2xl:w-20 pointer-events-none transition-all duration-700 ${
            demoVisible ? "opacity-100 translate-x-0 translate-y-0" : "opacity-0 translate-x-4 translate-y-4"
          }`}
          style={{ transitionDelay: '400ms', animation: demoVisible ? 'floatGentle 3s ease-in-out infinite' : 'none' }}
        />
      </div>
      
      <div className="w-full my-6 sm:my-8 md:my-10 lg:my-12" />
      
      {/* CTA section with staggered animations */}
      <div ref={ctaRef} className="flex flex-col space-y-3 sm:space-y-4 text-center px-4">
        <p 
          className={`text-white font-semibold text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-sourceSans font-black tracking-tighter transition-all duration-700 ${
            ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          Create scroll-stopping videos <span className="font-light italic">in</span>
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <p 
            className={`text-white font-light italic text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-sourceSans transition-all duration-700 ${
              ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            minutes.
          </p>
          <Link to="/templates">
            <button 
              className={`bg-ag-green hover:bg-ag-green/90 px-3 sm:px-4 py-1.5 sm:py-2 text-black rounded-lg transition-all duration-300 text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-sourceSans tracking-tighter font-bold hover:scale-105 hover:shadow-lg hover:shadow-ag-green/30 btn-press ripple-effect ${
                ctaVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-6 scale-90"
              }`}
              style={{ transitionDelay: '300ms' }}
            >
              Try for free
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DemoNow;
